import { Building, Eye, Edit3, Trash2, MapPin, DollarSign, ArrowUpDown, ArrowUp, ArrowDown, Columns } from 'lucide-react'
import { formatCurrency } from '../utils/formatCurrency'
import { useState, useEffect, useRef } from 'react'

interface Imovel {
    id: string
    nome: string
    endereco: string | null
    estado: string | null
    cidade: string | null
    tipo: string
    tipo_imovel: string
    valor_venda: number | null
    construtora_id: string | null
    unidades_disponiveis: number
    unidades_vendidas: number
    created_at: string
    construtora?: {
        nome: string
        cnpj: string
        contato_responsavel: string
    }
    unidades_count?: number
}

interface ImoveisListViewProps {
    imoveis: Imovel[]
    onView: (imovel: Imovel) => void
    onEdit: (imovel: Imovel) => void
    onDelete: (imovel: Imovel) => void
    formatDate: (date: string) => string
    searchTerm?: string
    sortColumn?: string | null
    sortDirection?: 'asc' | 'desc'
    onSort?: (column: string) => void
    visibleColumns?: Record<string, boolean>
    onToggleColumn?: (column: string) => void
}

export default function ImoveisListView({
    imoveis,
    onView,
    onEdit,
    onDelete,
    formatDate,
    searchTerm = '',
    sortColumn = null,
    sortDirection = 'asc',
    onSort,
    visibleColumns = {
        imovel: true,
        localizacao: true,
        tipo: true,
        construtora: true,
        valor: true,
        unidades: true,
        cadastro: true,
        acoes: true
    },
    onToggleColumn
}: ImoveisListViewProps) {
    const [showColumnMenu, setShowColumnMenu] = useState(false)
    const columnMenuRef = useRef<HTMLDivElement>(null)

    // Fechar menu ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
                setShowColumnMenu(false)
            }
        }

        if (showColumnMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showColumnMenu])

    const getSortIcon = (column: string) => {
        if (sortColumn !== column) {
            return <ArrowUpDown className="w-4 h-4 ml-1 text-gray-400" />
        }
        return sortDirection === 'asc'
            ? <ArrowUp className="w-4 h-4 ml-1 text-indigo-600 dark:text-indigo-400" />
            : <ArrowDown className="w-4 h-4 ml-1 text-indigo-600 dark:text-indigo-400" />
    }

    const handleSort = (column: string) => {
        if (onSort) {
            onSort(column)
        }
    }

    const columnLabels: Record<string, string> = {
        imovel: 'Imóvel',
        localizacao: 'Localização',
        tipo: 'Tipo',
        construtora: 'Construtora',
        valor: 'Valor',
        unidades: 'Unidades',
        cadastro: 'Cadastro',
        acoes: 'Ações'
    }
    if (imoveis.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Building className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Nenhum imóvel encontrado</h3>
                <p className="text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando seu primeiro imóvel'}
                </p>
            </div>
        )
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Menu de Colunas */}
            {onToggleColumn && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-end">
                    <div className="relative" ref={columnMenuRef}>
                        <button
                            onClick={() => setShowColumnMenu(!showColumnMenu)}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <Columns className="w-4 h-4" />
                            Colunas
                        </button>
                        {showColumnMenu && (
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                                <div className="p-2">
                                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2 py-1 mb-1">
                                        Exibir Colunas
                                    </div>
                                    {Object.entries(columnLabels).map(([key, label]) => (
                                        key !== 'acoes' && (
                                            <label
                                                key={key}
                                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns[key] !== false}
                                                    onChange={() => onToggleColumn(key)}
                                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                                            </label>
                                        )
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            {visibleColumns.imovel !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('nome')}
                                >
                                    <div className="flex items-center">
                                        Imóvel
                                        {onSort && getSortIcon('nome')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.localizacao !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('cidade')}
                                >
                                    <div className="flex items-center">
                                        Localização
                                        {onSort && getSortIcon('cidade')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.tipo !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('tipo')}
                                >
                                    <div className="flex items-center">
                                        Tipo
                                        {onSort && getSortIcon('tipo')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.construtora !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('construtora')}
                                >
                                    <div className="flex items-center">
                                        Construtora
                                        {onSort && getSortIcon('construtora')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.valor !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('valor_venda')}
                                >
                                    <div className="flex items-center">
                                        Valor
                                        {onSort && getSortIcon('valor_venda')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.unidades !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('unidades_disponiveis')}
                                >
                                    <div className="flex items-center">
                                        Unidades
                                        {onSort && getSortIcon('unidades_disponiveis')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.cadastro !== false && (
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => handleSort('created_at')}
                                >
                                    <div className="flex items-center">
                                        Cadastro
                                        {onSort && getSortIcon('created_at')}
                                    </div>
                                </th>
                            )}
                            {visibleColumns.acoes !== false && (
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Ações
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {imoveis.map((imovel) => (
                            <tr key={imovel.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                {visibleColumns.imovel !== false && (
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                                                <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {imovel.nome}
                                                </div>
                                                {imovel.endereco && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate">
                                                        {imovel.endereco}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.localizacao !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-1 text-sm text-gray-900 dark:text-gray-100">
                                            <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                            <span>
                                                {imovel.cidade && imovel.estado
                                                    ? `${imovel.cidade}, ${imovel.estado}`
                                                    : imovel.cidade || imovel.estado || '-'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.tipo !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-1">
                                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-800 dark:text-gray-300 inline-block w-fit">
                                                {imovel.tipo}
                                            </span>
                                            <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${imovel.tipo_imovel === 'novo'
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                                }`}>
                                                {imovel.tipo_imovel === 'novo' ? 'Novo' : 'Usado'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.construtora !== false && (
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                                            {imovel.construtora?.nome || '-'}
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.valor !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-1">
                                            <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                            <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                                {imovel.valor_venda ? formatCurrency(imovel.valor_venda) : 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.unidades !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">Vendidas:</span>
                                                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                                    {imovel.unidades_vendidas || 0}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">Disponíveis:</span>
                                                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                                    {imovel.unidades_disponiveis || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.cadastro !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 dark:text-gray-100">
                                            {formatDate(imovel.created_at)}
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.acoes !== false && (
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => onView(imovel)}
                                                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                                title="Visualizar"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => onEdit(imovel)}
                                                className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                                                title="Editar"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button
                                                onClick={() => onDelete(imovel)}
                                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                title="Excluir"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

